import React, { useState, useEffect } from 'react';
import { UserTable } from './UserTable';
import { EditModal } from './EditModal';
import { User as UserType, UserUpdateData, ManagerOption } from '../types';
import { SortingState } from '@tanstack/react-table';


interface UserGridDisplayProps {
  users: UserType[];
  managerOptions: ManagerOption[];
  onUpdateUser: (username: string, updates: UserUpdateData) => Promise<void>;
  onResetPassword: (username: string) => Promise<void>;
  onChangePassword: (username: string, password: string) => Promise<void>;
  onUnlockUser: (username: string) => Promise<void>;
  onDialogOpen?: () => void;
  onDialogClose?: () => void;
  pageSize: number;
  pageIndex: number;
  onPaginationChange: (pageIndex: number, pageSize: number) => void;
  onSortingChange: (sorting: SortingState | ((old: SortingState) => SortingState)) => void;
  sorting: SortingState;
  totalCount: number;
  loading: boolean;
  }

export const UserGridDisplay: React.FC<UserGridDisplayProps> = ({
	users,
	managerOptions,
	onUpdateUser,
  onResetPassword,
  onChangePassword,
  onUnlockUser,
  onDialogOpen,
  onDialogClose,
  pageSize,
  pageIndex,
  onPaginationChange,
  onSortingChange,
  sorting,
	totalCount,
	loading
}) => {
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [selectedTab, setSelectedTab] = useState<'info' | 'compliance' | 'security'>('info');
  const [visibleUsers, setVisibleUsers] = useState<UserType[]>(users);
  const [visibleTotalCount, setVisibleTotalCount] = useState<number>(totalCount);
  const stableLoading = loading && visibleUsers.length === 0;

  useEffect(() => {
    const hasIncomingUsers = Array.isArray(users) && users.length > 0;
    if (!loading) {
      setVisibleUsers(users);
      setVisibleTotalCount(totalCount);
      return;
    }

    if (!hasIncomingUsers && visibleUsers.length > 0) {
      return;
    }

    setVisibleUsers(users);
    setVisibleTotalCount(totalCount);
  }, [users, totalCount, loading, visibleUsers.length]);

  const handleUserSelect = (user: UserType, tab?: 'info' | 'compliance' | 'security') => {
    setSelectedUser(user);
    const nextTab = tab || 'info';
    setSelectedTab(nextTab);
    setIsEditModalOpen(true);
    onDialogOpen?.();
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
    setSelectedTab('info');
    onDialogClose?.();
  };

  return (
    <div className="space-y-4">
      <UserTable
        users={visibleUsers}
        onUserSelect={handleUserSelect}
        totalCount={visibleTotalCount}
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        sorting={sorting}
        loading={stableLoading}
      />

      <EditModal
        user={selectedUser}
        managerOptions={managerOptions}
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        onUpdateUser={onUpdateUser}
        onResetPassword={onResetPassword}
        onChangePassword={onChangePassword}
        onUnlockUser={onUnlockUser}
        initialTab={selectedTab}
      />
    </div>
  );
};

export default UserGridDisplay;
